import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Form, Modal, Result, Skeleton, Tabs, message, theme } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { ResultStatusType } from 'antd/es/result'
import B2BLayout from '../../../layouts/b2b'
import { useNavigationBlocker } from '../../../contexts/navigation-blocker-context'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { Permission, PermissionValue } from './utils'
import { getActiveRolePermissions, roleSettingsKey } from './utils/roleEditor'
import { RoleForm } from './components/RoleForm'
import { PermissionsEditor } from './components/PermissionsEditor'
import { RoleDashboardAccessEditor } from './components/RoleDashboardAccessEditor'
import { useRoleDashboardAccess } from './components/useRoleDashboardAccess'
import {
  DEFAULT_ROLE_NOTIFICATIONS,
  RoleNotificationsEditor,
  RoleNotificationSetting,
} from './components/RoleNotificationsEditor'
import './style.css'

interface RoleSettings {
  name?: string
  is_active: boolean
  start_page?: string
  priority?: number | null
  permissions: PermissionValue[]
  notifications: RoleNotificationSetting[]
}

const newRole = (): RoleSettings => ({
  is_active: true,
  permissions: [],
  notifications: DEFAULT_ROLE_NOTIFICATIONS,
})

export const AdminEditRole = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { token } = theme.useToken()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [accessError, setAccessError] = useState<IHttpError | null>(null)
  const [saveError, setSaveError] = useState('')
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [draft, setDraft] = useState<RoleSettings>(newRole)
  const [saved, setSaved] = useState<RoleSettings>(newRole)
  const [tab, setTab] = useState('general')
  const [reload, setReload] = useState(0)
  const requestId = useRef(0)
  const pageRef = useRef<HTMLDivElement>(null)
  const saveBarRef = useRef<HTMLElement>(null)
  const dashboard = useRoleDashboardAccess(id && id !== 'new' ? Number(id) : undefined)
  const dirty = roleSettingsKey(draft) !== roleSettingsKey(saved) || dashboard.dirty
  const breadcrumbs = useMemo(
    () => [
      { href: '/admin', title: 'Администрирование' },
      { href: '/admin/roles', title: 'Роли' },
    ],
    [],
  )

  useEffect(() => {
    let cancelled = false
    requestId.current++
    setLoading(true)
    setSaving(false)
    setAccessError(null)
    setSaveError('')
    setTab('general')
    Promise.all([
      fetchAPI('/api/roles/permissions-list', {}, true),
      id === 'new' ? Promise.resolve(null) : fetchAPI(`/api/roles/${id}`, {}, true),
    ])
      .then(([catalog, data]) => {
        if (cancelled) return
        if (catalog.error || data?.error) throw catalog.error || data?.error
        if (!Array.isArray(catalog) || (id !== 'new' && !data?.role))
          throw new Error('Некорректный ответ сервера')
        const values: RoleSettings = data
          ? {
              ...data.role,
              permissions: data.role_permissions || [],
              notifications: data.role_notifications || DEFAULT_ROLE_NOTIFICATIONS,
            }
          : newRole()
        setPermissions(catalog)
        setDraft(values)
        setSaved(values)
        form.resetFields()
        form.setFieldsValue(values)
      })
      .catch(error => {
        if (!cancelled)
          setAccessError({
            statusCode: error.statusCode || 500,
            message: error.message || 'Не удалось загрузить настройки роли',
          })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
      requestId.current++
    }
  }, [id, form, reload])

  useLayoutEffect(() => {
    const page = pageRef.current
    const saveBar = saveBarRef.current
    if (!page || !saveBar) return

    const updateLayout = () => {
      const { left, width } = page.getBoundingClientRect()
      saveBar.style.left = `${left}px`
      saveBar.style.width = `${width}px`
      page.style.setProperty(
        '--role-save-bar-height',
        `${Math.ceil(saveBar.getBoundingClientRect().height)}px`,
      )
    }
    const observer = new ResizeObserver(updateLayout)
    observer.observe(page)
    observer.observe(saveBar)
    window.addEventListener('resize', updateLayout)
    updateLayout()
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateLayout)
    }
  }, [loading, accessError])

  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  useEffect(() => {
    if (!dirty) return
    const confirmNavigation = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
      )
        return
      const link = (event.target as Element)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!link || link.target === '_blank' || link.hasAttribute('download')) return
      const url = new URL(link.href, window.location.href)
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return
      event.preventDefault()
      event.stopPropagation()
      if (saving) return
      Modal.confirm({
        title: 'Уйти без сохранения?',
        content: 'Несохранённые изменения роли будут потеряны.',
        okText: 'Уйти',
        cancelText: 'Остаться',
        onOk: () => navigate(url.pathname + url.search + url.hash),
      })
    }
    document.addEventListener('click', confirmNavigation, true)
    return () => document.removeEventListener('click', confirmNavigation, true)
  }, [dirty, saving, navigate])

  const updateDraft = (values: Partial<RoleSettings>) => {
    setDraft(current => ({ ...current, ...values }))
    form.setFieldsValue(values)
  }

  const discard = () =>
    Modal.confirm({
      title: 'Отменить несохранённые изменения?',
      content: 'Будут восстановлены последние сохранённые настройки во всех вкладках.',
      okText: 'Отменить изменения',
      cancelText: 'Продолжить редактирование',
      onOk: () => {
        form.resetFields()
        form.setFieldsValue(saved)
        setDraft(saved)
        dashboard.reset()
        setSaveError('')
      },
    })

  const save = async () => {
    if (saving || (id !== 'new' && !dirty)) return
    try {
      await form.validateFields()
    } catch {
      setTab('general')
      return
    }
    const request = requestId.current
    const values = {
      ...draft,
      permissions: getActiveRolePermissions(permissions, draft.permissions),
    }
    setSaving(true)
    setSaveError('')
    try {
      const result = await fetchAPI(
        `/api/roles/${id === 'new' ? 'new' : `update/${id}`}`,
        {
          method: 'POST',
          body: JSON.stringify({
            ...values,
            ...(dashboard.dirty
              ? { dashboard_access: { groups: dashboard.grants, revision: dashboard.revision } }
              : {}),
          }),
        },
        true,
      )
      if (request !== requestId.current) return
      if (result.error || !result.success) {
        setSaveError(result.error?.message || 'Не удалось сохранить настройки')
        if (result.error?.statusCode === 409) setTab('svetofor')
        return
      }
      setSaved(values)
      setDraft(values)
      form.setFieldsValue(values)
      if (result.dashboard_access) dashboard.acceptSaved(result.dashboard_access)
      message.success('Настройки роли сохранены')
      if (id === 'new' && result.id) navigate(`/admin/role/${result.id}`, { replace: true })
    } catch (error) {
      if (request === requestId.current)
        setSaveError((error as Error).message || 'Не удалось сохранить настройки')
    } finally {
      if (request === requestId.current) setSaving(false)
    }
  }

  useNavigationBlocker(dirty || saving, () => {
    if (saving) return Promise.resolve(false)
    return new Promise<boolean>(resolve => {
      Modal.confirm({
        title: 'Уйти без сохранения?',
        content: 'Несохранённые изменения роли будут потеряны.',
        okText: 'Уйти',
        cancelText: 'Остаться',
        onOk: () => {
          resolve(true)
        },
        onCancel: () => {
          resolve(false)
        },
      })
    })
  })

  const pageTitle = loading || accessError ? 'Настройка роли' : draft.name?.trim() || 'Новая роль'

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle} backHref="/admin/roles">
      {loading ? (
        <Skeleton active />
      ) : accessError ? (
        <Result
          status={
            (accessError.statusCode === 403 || accessError.statusCode === 404
              ? accessError.statusCode
              : 'error') as ResultStatusType
          }
          title="Не удалось открыть роль"
          subTitle={accessError.message}
          extra={<Button onClick={() => setReload(value => value + 1)}>Повторить</Button>}
        />
      ) : (
        <div
          ref={pageRef}
          className="role-settings-page"
          style={
            {
              '--role-accent': token.colorPrimary,
              '--role-text': token.colorText,
              '--role-text-secondary': token.colorTextSecondary,
              '--role-border': token.colorBorderSecondary,
              '--role-surface': token.colorBgContainer,
              '--role-fill': token.colorFillAlter,
              '--role-hover': token.colorFillTertiary,
              '--role-success': token.colorSuccessText,
              '--role-success-bg': token.colorSuccessBg,
            } as React.CSSProperties
          }
        >
          <Form
            form={form}
            layout="vertical"
            disabled={saving}
            onValuesChange={(_, values) => setDraft(current => ({ ...current, ...values }))}
            onFinish={save}
            className="role-settings-form"
          >
            <Tabs
              activeKey={tab}
              onChange={setTab}
              items={[
                { key: 'general', label: 'Основное', forceRender: true, children: <RoleForm /> },
                {
                  key: 'permissions',
                  label: 'Права доступа',
                  forceRender: true,
                  children: (
                    <PermissionsEditor
                      permissions={permissions}
                      currentPermissions={draft.permissions}
                      onPermissionChange={values => updateDraft({ permissions: values })}
                    />
                  ),
                },
                {
                  key: 'svetofor',
                  label: 'Светофор',
                  forceRender: true,
                  children: (
                    <RoleDashboardAccessEditor
                      roleId={id === 'new' ? undefined : Number(id)}
                      model={dashboard}
                      saving={saving}
                    />
                  ),
                },
                {
                  key: 'notifications',
                  label: 'Уведомления',
                  forceRender: true,
                  children: (
                    <RoleNotificationsEditor
                      notifications={draft.notifications}
                      onChange={values => updateDraft({ notifications: values })}
                    />
                  ),
                },
              ]}
            />
            {saveError && (
              <Alert
                className="role-save-error"
                showIcon
                type="error"
                message={saveError}
                action={
                  tab === 'svetofor' ? (
                    <Button
                      htmlType="button"
                      onClick={() =>
                        Modal.confirm({
                          title: 'Загрузить актуальный доступ к Светофору?',
                          content:
                            'Несохранённый выбор показателей будет заменён. Изменения в других вкладках сохранятся.',
                          okText: 'Загрузить',
                          cancelText: 'Отмена',
                          onOk: () => {
                            dashboard.reload()
                            setSaveError('')
                          },
                        })
                      }
                    >
                      Загрузить актуальные
                    </Button>
                  ) : undefined
                }
              />
            )}
            <footer ref={saveBarRef} className="role-save-bar">
              <span className={`role-save-status${dirty ? ' is-dirty' : ''}`} role="status">
                {dirty ? (
                  <>
                    <i />
                    Есть несохранённые изменения
                  </>
                ) : (
                  <>
                    <CheckCircleOutlined />
                    {id === 'new' ? 'Заполните настройки новой роли' : 'Все изменения сохранены'}
                  </>
                )}
              </span>
              <div className="role-save-actions">
                <Button htmlType="button" onClick={discard} disabled={!dirty || saving}>
                  Отменить изменения
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  disabled={id !== 'new' && !dirty}
                >
                  Сохранить изменения
                </Button>
              </div>
            </footer>
          </Form>
        </div>
      )}
    </B2BLayout>
  )
}
